/**
 * Radix-backed Dialog primitives.
 *
 * Replaces the earlier plain-DOM stub. The library owns its dialog rendering —
 * consumers do NOT need to override this via Vite alias.
 *
 * Consumers must provide an auth-aware `httpClient` prop to FormCacheSelector.
 */

import { Dialog as DialogPrimitive } from 'radix-ui';
import type { ReactNode, ComponentPropsWithoutRef, ElementRef } from 'react';
import * as React from 'react';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}

export function Dialog({ open, onOpenChange, children }: DialogProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </DialogPrimitive.Root>
  );
}

export interface DialogContentProps {
  children: ReactNode;
  className?: string;
}

export function DialogContent({ children, ...props }: DialogContentProps & Record<string, unknown>) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50" />
      <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-xl" {...props}>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export interface DialogHeaderProps {
  children: ReactNode;
}

export function DialogHeader({ children }: DialogHeaderProps) {
  return <div className="mb-4">{children}</div>;
}

export interface DialogTitleProps {
  children: ReactNode;
}

export function DialogTitle({ children }: DialogTitleProps) {
  return (
    <DialogPrimitive.Title className="text-lg font-semibold text-neutral-900">
      {children}
    </DialogPrimitive.Title>
  );
}

export interface DialogDescriptionProps {
  children: ReactNode;
}

export function DialogDescription({ children }: DialogDescriptionProps) {
  return (
    <DialogPrimitive.Description className="text-sm text-neutral-500">
      {children}
    </DialogPrimitive.Description>
  );
}

export interface DialogFooterProps {
  children: ReactNode;
}

export function DialogFooter({ children }: DialogFooterProps) {
  return <div className="mt-6 flex justify-end gap-2">{children}</div>;
}

export const DialogClose = React.forwardRef<
  ElementRef<typeof DialogPrimitive.Close>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className = '', ...props }, ref) => (
  <DialogPrimitive.Close
    ref={ref}
    className={['rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400', className].filter(Boolean).join(' ')}
    {...props}
  />
));
DialogClose.displayName = 'DialogClose';
